const privacyTranslations = {
  en: {
    back: "← Back to Home",
    eyebrow: "PRIVACY",
    title: "Privacy Policy",
    effectiveDate: "Effective Date: September 5, 2026",
    translationNotice: null,

    intro:
      "ACE Massage Hub respects your privacy. This Privacy Policy explains how information may be collected, used, stored, and shared when you use the Massage Exam Practice App, ACE Massage Hub website, subscriptions, learning tools, or related services.",

    sections: [
      {
        title: "1. Information We Collect",
        paragraphs: [
          "We may collect information that you provide directly, including your name, email address, account information, support messages, and other information you submit through the platform.",
          "We may also collect information generated through your use of the platform, including practice activity, answers, question progress, wrong questions, learning progress, vocabulary activity, AI explanation usage, subscription status, and related learning data.",
        ],
      },
      {
        title: "2. Account and Authentication Information",
        paragraphs: [
          "Account authentication and certain application data may be processed and stored using third-party infrastructure providers such as Supabase.",
          "Passwords are handled through authentication systems and are not intended to be stored by ACE Massage Hub in plain text.",
        ],
      },
      {
        title: "3. Payment Information",
        paragraphs: [
          "Subscription payments may be processed through Stripe or another payment processor.",
          "ACE Massage Hub does not intend to directly store complete credit card numbers. Payment processors may collect and process payment information according to their own privacy and security policies.",
        ],
      },
      {
        title: "4. How We Use Information",
        paragraphs: [
          "We may use information to provide and operate the service, authenticate accounts, maintain learning progress, personalize practice experiences, administer subscriptions, provide customer support, improve platform features, detect misuse, maintain security, and communicate with users.",
        ],
      },
      {
        title: "5. Learning and Practice Data",
        paragraphs: [
          "Practice history, question performance, wrong questions, mastery indicators, topic progress, vocabulary progress, and similar educational data may be stored so that the platform can provide personalized study features and restore your progress across sessions.",
        ],
      },
      {
        title: "6. AI Features",
        paragraphs: [
          "When you request AI-generated explanations, information necessary to generate the explanation may be sent to an AI service provider.",
          "We seek to limit information sent to AI providers to what is reasonably necessary to provide the requested feature. Users should not submit highly sensitive personal information through AI explanation features.",
        ],
      },
      {
        title: "7. Support Communications",
        paragraphs: [
          "If you contact support, we may collect your name, email address, message, support category, and any information you voluntarily provide so that we can respond to your request.",
        ],
      },
      {
        title: "8. Service Providers",
        paragraphs: [
          "We may use third-party providers to operate portions of the service, including hosting, authentication, databases, payment processing, email delivery, analytics, and AI functionality.",
          "These providers may process information as needed to perform services on our behalf and are subject to their own contractual, privacy, and security obligations.",
        ],
      },
      {
        title: "9. Cookies and Local Storage",
        paragraphs: [
          "The application may use browser storage, including local storage or similar technologies, to remember preferences, navigation state, language settings, session-related information, and other application functionality.",
        ],
      },
      {
        title: "10. Data Retention",
        paragraphs: [
          "We may retain account and learning data for as long as reasonably necessary to provide the service, maintain user progress, meet legal or accounting obligations, resolve disputes, prevent fraud, and protect the platform.",
        ],
      },
      {
        title: "11. Data Security",
        paragraphs: [
          "We use reasonable administrative, technical, and organizational measures intended to protect information. However, no online service, database, or transmission method can be guaranteed to be completely secure.",
        ],
      },
      {
        title: "12. Data Sharing",
        paragraphs: [
          "We do not sell personal information as a normal part of operating ACE Massage Hub.",
          "Information may be shared with service providers, professional advisers, authorities where legally required, or in connection with a business transfer, merger, acquisition, financing, or sale of assets.",
        ],
      },
      {
        title: "13. Your Choices",
        paragraphs: [
          "You may contact us to ask questions about your account information or request assistance with updating or deleting certain information, subject to applicable legal, security, recordkeeping, and operational requirements.",
        ],
      },
      {
        title: "14. Children's Privacy",
        paragraphs: [
          "The service is not intended for children under 13. We do not knowingly seek to collect personal information from children under 13.",
        ],
      },
      {
        title: "15. Changes to This Privacy Policy",
        paragraphs: [
          "We may update this Privacy Policy as the platform, technology, legal requirements, or business practices change. Updated versions will be posted with a revised effective date.",
        ],
      },
      {
        title: "16. Contact Us",
        paragraphs: [
          "If you have questions about this Privacy Policy or your personal information, contact:",
        ],
      },
    ],

    contactEmail: "supports@acemassagehub.com",
  },

  zh: {
    back: "← 返回首页",
    eyebrow: "隐私",
    title: "隐私政策",
    effectiveDate: "生效日期：2026年9月5日",

    translationNotice:
      "本中文版本仅为方便阅读而提供。如本翻译版本与英文版本存在任何不一致或歧义，应以英文版本为准。",

    intro:
      "ACE Massage Hub 尊重您的隐私。本隐私政策说明当您使用 Massage Exam Practice App、ACE Massage Hub 网站、订阅服务、学习工具或相关服务时，我们可能如何收集、使用、存储和共享信息。",

    sections: [
      {
        title: "1. 我们收集的信息",
        paragraphs: [
          "我们可能收集您直接提供的信息，包括您的姓名、电子邮箱地址、账户信息、客服消息，以及您通过平台提交的其他信息。",
          "我们还可能收集您在使用平台过程中产生的信息，包括练习活动、答题记录、题目进度、错题记录、学习进度、词汇学习活动、AI 解释功能使用情况、订阅状态以及相关学习数据。",
        ],
      },
      {
        title: "2. 账户和身份验证信息",
        paragraphs: [
          "账户身份验证及部分应用数据可能通过 Supabase 等第三方基础设施服务商进行处理和存储。",
          "密码通过身份验证系统进行处理，ACE Massage Hub 无意以明文形式存储您的密码。",
        ],
      },
      {
        title: "3. 支付信息",
        paragraphs: [
          "订阅付款可能通过 Stripe 或其他支付处理服务商进行处理。",
          "ACE Massage Hub 无意直接存储完整的信用卡号码。支付处理服务商可能根据其自身的隐私和安全政策收集并处理支付信息。",
        ],
      },
      {
        title: "4. 我们如何使用信息",
        paragraphs: [
          "我们可能使用相关信息来提供和运营服务、验证账户、保存学习进度、个性化练习体验、管理订阅、提供客户支持、改进平台功能、检测滥用行为、维护安全以及与用户进行沟通。",
        ],
      },
      {
        title: "5. 学习和练习数据",
        paragraphs: [
          "练习历史、题目表现、错题、掌握程度指标、主题学习进度、词汇学习进度及类似教育数据可能会被存储，以便平台提供个性化学习功能，并在不同使用会话之间恢复您的学习进度。",
        ],
      },
      {
        title: "6. AI 功能",
        paragraphs: [
          "当您请求 AI 生成的解释时，生成该解释所必需的信息可能会被发送给 AI 服务提供商。",
          "我们会尽量将发送给 AI 服务提供商的信息限制在提供所请求功能所合理必要的范围内。用户不应通过 AI 解释功能提交高度敏感的个人信息。",
        ],
      },
      {
        title: "7. 客服沟通",
        paragraphs: [
          "如果您联系客服，我们可能收集您的姓名、电子邮箱地址、消息内容、客服类别以及您自愿提供的其他信息，以便我们回复您的请求。",
        ],
      },
      {
        title: "8. 服务提供商",
        paragraphs: [
          "我们可能使用第三方服务提供商运营服务的部分功能，包括托管、身份验证、数据库、支付处理、电子邮件发送、分析以及 AI 功能。",
          "这些服务提供商可能在代表我们提供服务所必要的范围内处理信息，并受其自身合同、隐私和安全义务的约束。",
        ],
      },
      {
        title: "9. Cookie 和本地存储",
        paragraphs: [
          "本应用可能使用浏览器存储，包括本地存储或类似技术，用于记住偏好设置、导航状态、语言设置、与会话相关的信息以及支持其他应用功能。",
        ],
      },
      {
        title: "10. 数据保留",
        paragraphs: [
          "我们可能在合理必要的期限内保留账户和学习数据，以提供服务、保存用户进度、履行法律或会计义务、解决争议、防止欺诈以及保护平台。",
        ],
      },
      {
        title: "11. 数据安全",
        paragraphs: [
          "我们采取合理的管理、技术和组织措施，以保护相关信息。但是，任何在线服务、数据库或数据传输方式都无法保证绝对安全。",
        ],
      },
      {
        title: "12. 数据共享",
        paragraphs: [
          "作为 ACE Massage Hub 正常运营的一部分，我们不会出售个人信息。",
          "在提供服务、获得专业顾问支持、法律要求，或涉及业务转让、合并、收购、融资或资产出售的情况下，信息可能会与服务提供商、专业顾问或有关政府机构共享。",
        ],
      },
      {
        title: "13. 您的选择",
        paragraphs: [
          "您可以联系我们，咨询您的账户信息，或请求协助更新或删除某些信息，但相关请求可能受到适用法律、安全、记录保存和运营要求的限制。",
        ],
      },
      {
        title: "14. 儿童隐私",
        paragraphs: [
          "本服务并非面向 13 岁以下儿童。我们不会有意收集 13 岁以下儿童的个人信息。",
        ],
      },
      {
        title: "15. 本隐私政策的变更",
        paragraphs: [
          "随着平台、技术、法律要求或业务实践发生变化，我们可能会更新本隐私政策。更新后的版本将发布，并注明修订后的生效日期。",
        ],
      },
      {
        title: "16. 联系我们",
        paragraphs: [
          "如果您对本隐私政策或您的个人信息有任何疑问，请联系：",
        ],
      },
    ],

    contactEmail: "supports@acemassagehub.com",
  },

  es: {
    back: "← Volver al inicio",
    eyebrow: "PRIVACIDAD",
    title: "Política de privacidad",
    effectiveDate: "Fecha de entrada en vigor: 5 de septiembre de 2026",

    translationNotice:
      "Esta traducción se proporciona únicamente para facilitar su lectura. En caso de discrepancia o ambigüedad entre esta traducción y la versión en inglés, prevalecerá la versión en inglés.",

    intro:
      "ACE Massage Hub respeta su privacidad. Esta Política de privacidad explica cómo se puede recopilar, utilizar, almacenar y compartir información cuando utiliza Massage Exam Practice App, el sitio web de ACE Massage Hub, suscripciones, herramientas de aprendizaje u otros servicios relacionados.",

    sections: [
      {
        title: "1. Información que recopilamos",
        paragraphs: [
          "Podemos recopilar información que usted proporcione directamente, incluido su nombre, dirección de correo electrónico, información de la cuenta, mensajes de soporte y otra información que envíe a través de la plataforma.",
          "También podemos recopilar información generada mediante el uso de la plataforma, incluida la actividad de práctica, respuestas, progreso de preguntas, preguntas respondidas incorrectamente, progreso de aprendizaje, actividad de vocabulario, uso de explicaciones de IA, estado de suscripción y datos de aprendizaje relacionados.",
        ],
      },
      {
        title: "2. Información de cuenta y autenticación",
        paragraphs: [
          "La autenticación de cuentas y determinados datos de la aplicación pueden ser procesados y almacenados mediante proveedores externos de infraestructura como Supabase.",
          "Las contraseñas se gestionan mediante sistemas de autenticación y ACE Massage Hub no pretende almacenarlas en texto sin formato.",
        ],
      },
      {
        title: "3. Información de pago",
        paragraphs: [
          "Los pagos de suscripciones pueden procesarse mediante Stripe u otro procesador de pagos.",
          "ACE Massage Hub no pretende almacenar directamente números completos de tarjetas de crédito. Los procesadores de pagos pueden recopilar y procesar información de pago de acuerdo con sus propias políticas de privacidad y seguridad.",
        ],
      },
      {
        title: "4. Cómo utilizamos la información",
        paragraphs: [
          "Podemos utilizar la información para proporcionar y operar el servicio, autenticar cuentas, mantener el progreso de aprendizaje, personalizar las experiencias de práctica, administrar suscripciones, proporcionar atención al cliente, mejorar las funciones de la plataforma, detectar usos indebidos, mantener la seguridad y comunicarnos con los usuarios.",
        ],
      },
      {
        title: "5. Datos de aprendizaje y práctica",
        paragraphs: [
          "El historial de práctica, rendimiento en preguntas, preguntas incorrectas, indicadores de dominio, progreso por temas, progreso de vocabulario y datos educativos similares pueden almacenarse para que la plataforma pueda proporcionar funciones de estudio personalizadas y restaurar su progreso entre sesiones.",
        ],
      },
      {
        title: "6. Funciones de IA",
        paragraphs: [
          "Cuando solicita explicaciones generadas por IA, la información necesaria para generar la explicación puede enviarse a un proveedor de servicios de IA.",
          "Procuramos limitar la información enviada a los proveedores de IA a lo razonablemente necesario para proporcionar la función solicitada. Los usuarios no deben enviar información personal altamente sensible mediante las funciones de explicación de IA.",
        ],
      },
      {
        title: "7. Comunicaciones con soporte",
        paragraphs: [
          "Si se comunica con soporte, podemos recopilar su nombre, dirección de correo electrónico, mensaje, categoría de soporte y cualquier información que proporcione voluntariamente para poder responder a su solicitud.",
        ],
      },
      {
        title: "8. Proveedores de servicios",
        paragraphs: [
          "Podemos utilizar proveedores externos para operar partes del servicio, incluidos alojamiento, autenticación, bases de datos, procesamiento de pagos, entrega de correo electrónico, análisis y funciones de IA.",
          "Estos proveedores pueden procesar información según sea necesario para prestar servicios en nuestro nombre y están sujetos a sus propias obligaciones contractuales, de privacidad y seguridad.",
        ],
      },
      {
        title: "9. Cookies y almacenamiento local",
        paragraphs: [
          "La aplicación puede utilizar almacenamiento del navegador, incluido almacenamiento local o tecnologías similares, para recordar preferencias, estado de navegación, configuración de idioma, información relacionada con sesiones y otras funciones de la aplicación.",
        ],
      },
      {
        title: "10. Conservación de datos",
        paragraphs: [
          "Podemos conservar datos de cuenta y aprendizaje durante el tiempo razonablemente necesario para proporcionar el servicio, mantener el progreso del usuario, cumplir obligaciones legales o contables, resolver disputas, prevenir fraudes y proteger la plataforma.",
        ],
      },
      {
        title: "11. Seguridad de los datos",
        paragraphs: [
          "Utilizamos medidas administrativas, técnicas y organizativas razonables destinadas a proteger la información. Sin embargo, no se puede garantizar que ningún servicio en línea, base de datos o método de transmisión sea completamente seguro.",
        ],
      },
      {
        title: "12. Intercambio de datos",
        paragraphs: [
          "No vendemos información personal como parte normal de las operaciones de ACE Massage Hub.",
          "La información puede compartirse con proveedores de servicios, asesores profesionales, autoridades cuando así lo exija la ley, o en relación con una transferencia empresarial, fusión, adquisición, financiación o venta de activos.",
        ],
      },
      {
        title: "13. Sus opciones",
        paragraphs: [
          "Puede comunicarse con nosotros para hacer preguntas sobre la información de su cuenta o solicitar asistencia para actualizar o eliminar determinada información, sujeto a los requisitos legales, de seguridad, conservación de registros y operativos aplicables.",
        ],
      },
      {
        title: "14. Privacidad de los menores",
        paragraphs: [
          "El servicio no está destinado a menores de 13 años. No buscamos recopilar deliberadamente información personal de menores de 13 años.",
        ],
      },
      {
        title: "15. Cambios en esta Política de privacidad",
        paragraphs: [
          "Podemos actualizar esta Política de privacidad a medida que cambien la plataforma, la tecnología, los requisitos legales o las prácticas comerciales. Las versiones actualizadas se publicarán con una fecha de entrada en vigor revisada.",
        ],
      },
      {
        title: "16. Contáctenos",
        paragraphs: [
          "Si tiene preguntas sobre esta Política de privacidad o sobre su información personal, comuníquese con:",
        ],
      },
    ],

    contactEmail: "supports@acemassagehub.com",
  },

  ru: {
    back: "← Вернуться на главную",
    eyebrow: "КОНФИДЕНЦИАЛЬНОСТЬ",
    title: "Политика конфиденциальности",
    effectiveDate: "Дата вступления в силу: 5 сентября 2026 г.",

    translationNotice:
      "Этот перевод предоставляется исключительно для удобства чтения. В случае любых расхождений или неоднозначностей между данным переводом и английской версией преимущественную силу имеет английская версия.",

    intro:
      "ACE Massage Hub уважает вашу конфиденциальность. Настоящая Политика конфиденциальности объясняет, каким образом информация может собираться, использоваться, храниться и передаваться при использовании Massage Exam Practice App, веб-сайта ACE Massage Hub, подписок, учебных инструментов или связанных услуг.",

    sections: [
      {
        title: "1. Информация, которую мы собираем",
        paragraphs: [
          "Мы можем собирать информацию, которую вы предоставляете непосредственно, включая ваше имя, адрес электронной почты, информацию об учетной записи, сообщения в службу поддержки и другую информацию, которую вы отправляете через платформу.",
          "Мы также можем собирать информацию, создаваемую в процессе использования платформы, включая данные о практических занятиях, ответах, прогрессе по вопросам, неправильно отвеченных вопросах, учебном прогрессе, работе со словарем, использовании объяснений ИИ, статусе подписки и связанных учебных данных.",
        ],
      },
      {
        title: "2. Учетная запись и аутентификация",
        paragraphs: [
          "Аутентификация учетной записи и определенные данные приложения могут обрабатываться и храниться с использованием сторонних поставщиков инфраструктуры, таких как Supabase.",
          "Пароли обрабатываются системами аутентификации, и ACE Massage Hub не предполагает хранить их в виде открытого текста.",
        ],
      },
      {
        title: "3. Платежная информация",
        paragraphs: [
          "Платежи за подписку могут обрабатываться через Stripe или другого платежного оператора.",
          "ACE Massage Hub не предполагает напрямую хранить полные номера кредитных карт. Платежные операторы могут собирать и обрабатывать платежную информацию в соответствии со своими политиками конфиденциальности и безопасности.",
        ],
      },
      {
        title: "4. Как мы используем информацию",
        paragraphs: [
          "Мы можем использовать информацию для предоставления и работы сервиса, аутентификации учетных записей, сохранения учебного прогресса, персонализации практических занятий, управления подписками, оказания поддержки, улучшения функций платформы, выявления неправомерного использования, обеспечения безопасности и связи с пользователями.",
        ],
      },
      {
        title: "5. Данные обучения и практики",
        paragraphs: [
          "История практики, результаты ответов, неправильно отвеченные вопросы, показатели освоения материала, прогресс по темам, прогресс изучения словаря и аналогичные образовательные данные могут храниться, чтобы платформа могла предоставлять персонализированные функции обучения и восстанавливать ваш прогресс между сеансами.",
        ],
      },
      {
        title: "6. Функции искусственного интеллекта",
        paragraphs: [
          "Когда вы запрашиваете объяснение, созданное ИИ, информация, необходимая для его создания, может быть отправлена поставщику услуг искусственного интеллекта.",
          "Мы стремимся ограничивать информацию, отправляемую поставщикам ИИ, объемом, разумно необходимым для предоставления запрошенной функции. Пользователям не следует отправлять особо конфиденциальную личную информацию через функции объяснений ИИ.",
        ],
      },
      {
        title: "7. Обращения в службу поддержки",
        paragraphs: [
          "Если вы обращаетесь в службу поддержки, мы можем собирать ваше имя, адрес электронной почты, текст сообщения, категорию обращения и любую информацию, которую вы добровольно предоставляете, чтобы мы могли ответить на ваш запрос.",
        ],
      },
      {
        title: "8. Поставщики услуг",
        paragraphs: [
          "Мы можем использовать сторонних поставщиков для работы отдельных частей сервиса, включая хостинг, аутентификацию, базы данных, обработку платежей, доставку электронной почты, аналитику и функции ИИ.",
          "Эти поставщики могут обрабатывать информацию в объеме, необходимом для оказания услуг от нашего имени, и несут собственные договорные обязательства, а также обязательства в области конфиденциальности и безопасности.",
        ],
      },
      {
        title: "9. Файлы cookie и локальное хранилище",
        paragraphs: [
          "Приложение может использовать хранилище браузера, включая локальное хранилище или аналогичные технологии, чтобы запоминать настройки, состояние навигации, языковые настройки, информацию, связанную с сеансами, и обеспечивать другие функции приложения.",
        ],
      },
      {
        title: "10. Хранение данных",
        paragraphs: [
          "Мы можем хранить данные учетной записи и обучения столько времени, сколько это разумно необходимо для предоставления сервиса, сохранения прогресса пользователя, выполнения юридических или бухгалтерских обязательств, разрешения споров, предотвращения мошенничества и защиты платформы.",
        ],
      },
      {
        title: "11. Безопасность данных",
        paragraphs: [
          "Мы применяем разумные административные, технические и организационные меры, предназначенные для защиты информации. Однако невозможно гарантировать абсолютную безопасность любого онлайн-сервиса, базы данных или способа передачи информации.",
        ],
      },
      {
        title: "12. Передача данных",
        paragraphs: [
          "Мы не продаем персональную информацию в рамках обычной деятельности ACE Massage Hub.",
          "Информация может передаваться поставщикам услуг, профессиональным консультантам, государственным органам, когда этого требует закон, либо в связи с передачей бизнеса, слиянием, приобретением, финансированием или продажей активов.",
        ],
      },
      {
        title: "13. Ваши возможности",
        paragraphs: [
          "Вы можете связаться с нами с вопросами об информации вашей учетной записи или запросить помощь в обновлении или удалении определенной информации с учетом применимых юридических требований, требований безопасности, хранения документации и операционных требований.",
        ],
      },
      {
        title: "14. Конфиденциальность детей",
        paragraphs: [
          "Сервис не предназначен для детей младше 13 лет. Мы сознательно не стремимся собирать персональную информацию детей младше 13 лет.",
        ],
      },
      {
        title: "15. Изменения настоящей Политики конфиденциальности",
        paragraphs: [
          "Мы можем обновлять настоящую Политику конфиденциальности по мере изменения платформы, технологий, юридических требований или деловой практики. Обновленные версии будут публиковаться с пересмотренной датой вступления в силу.",
        ],
      },
      {
        title: "16. Связаться с нами",
        paragraphs: [
          "Если у вас есть вопросы о настоящей Политике конфиденциальности или вашей персональной информации, свяжитесь с нами:",
        ],
      },
    ],

    contactEmail: "supports@acemassagehub.com",
  },
};

export default privacyTranslations;